import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { dispatchChatbotOpen, getChatbotModerationMessage, isChatbotEnabled, isIskraRelated, normalizeSuggestionItems } from '../Chatbot/helpers';
import { chatbotMessages, chatbotPromptMessages } from '../Chatbot/messages';

export const View = ({ data, isEditMode = false }) => {
  const intl = useIntl();
  const inputRef = React.useRef(null);
  const [feedbackMessage, setFeedbackMessage] = React.useState('');

  const chatbotEnabled = isChatbotEnabled();
  const defaultSuggestions = React.useMemo(() => chatbotPromptMessages.map((descriptor) => intl.formatMessage(descriptor)), [intl]);
  const suggestions = normalizeSuggestionItems(data?.suggestions?.length ? data.suggestions : defaultSuggestions);
  const title = data?.title || intl.formatMessage(chatbotMessages.defaultHomeTitle);
  const description = data?.description || intl.formatMessage(chatbotMessages.defaultHomeDescription);
  const placeholder = data?.placeholder || intl.formatMessage(chatbotMessages.defaultHomePlaceholder);
  const submitLabel = intl.formatMessage(chatbotMessages.submit);
  const suggestionsLabel = intl.formatMessage(chatbotMessages.suggestionsLabel);
  const unavailableMessage = intl.formatMessage(chatbotMessages.unavailableMessage);
  const editModeWarning = intl.formatMessage(chatbotMessages.editModeWarning);

  const handleSubmit = React.useCallback(
    (question) => {
      const trimmedQuestion = question?.trim();

      if (!trimmedQuestion) {
        return;
      }

      if (!chatbotEnabled) {
        setFeedbackMessage(unavailableMessage);
        return;
      }

      if (!isIskraRelated(trimmedQuestion)) {
        setFeedbackMessage(getChatbotModerationMessage(intl));
        return;
      }

      setFeedbackMessage('');
      dispatchChatbotOpen(trimmedQuestion);

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [chatbotEnabled, intl, unavailableMessage],
  );

  if (!chatbotEnabled && !isEditMode) {
    return null;
  }

  return (
    <div className={`iskra-chatbot-home ${data?.color || 'primary'}`}>
      <div className="iskra-chatbot-home__container">
        <div className="iskra-chatbot-home__heading">
          <span className="iskra-chatbot-home__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 8h10M7 12h7" />
              <path d="M5 19l3-3h9a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3" />
            </svg>
          </span>
          <div>
            <h2 className="iskra-chatbot-home__title">{title}</h2>
            {description && <p className="iskra-chatbot-home__description">{description}</p>}
          </div>
        </div>

        <div className="iskra-chatbot-home__input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="iskra-chatbot-home__input"
            placeholder={placeholder}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSubmit(event.currentTarget.value);
              }
            }}
          />

          <button type="button" className="iskra-chatbot-home__submit" onClick={() => handleSubmit(inputRef.current?.value)} aria-label={submitLabel}>
            <span>{submitLabel}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {feedbackMessage && <p className="iskra-chatbot-home__feedback is-error">{feedbackMessage}</p>}

        {suggestions.length > 0 && (
          <div className="iskra-chatbot-home__suggestions">
            <span className="iskra-chatbot-home__suggestions-label">{suggestionsLabel}</span>
            <div className="iskra-chatbot-home__suggestions-list">
              {suggestions.map((suggestion, index) => (
                <button key={`${suggestion}-${index}`} type="button" className="iskra-chatbot-home__suggestion" onClick={() => handleSubmit(suggestion)}>
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {!chatbotEnabled && isEditMode && <p className="iskra-chatbot-home__feedback is-warning">{editModeWarning}</p>}
      </div>
    </div>
  );
};

View.propTypes = {
  data: PropTypes.shape({
    color: PropTypes.string,
    description: PropTypes.string,
    placeholder: PropTypes.string,
    suggestions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    title: PropTypes.string,
  }),
  isEditMode: PropTypes.bool,
};

export default View;
